import { DndContext } from '@dnd-kit/core'
import { FileDropzoneRejectReason, useFileDropzone, useTranslation } from '@msdk/common'
import { AttachFileIcon, ButtonIcon, ButtonSecondary, Card, CardHeader, IconSelector, styled, uiTokens } from '@msdk/ui'
import { CardContent, Typography } from '@mui/material'
import { useRef } from 'react'

interface PhotoDragAndDropProps {
  accept?: string
  maxSize: number
  multiple?: boolean
  onFiles: (file?: File) => void
  onFilesReject?: (file: File, reason: FileDropzoneRejectReason) => void
  selectedFile?: File
  onClear: () => void
  isUploadCompleted?: boolean
}

const DropArea = styled('div')<{ isDragging: boolean }>`
  border: ${uiTokens.sizing.stroke.light}px dashed ${uiTokens.uiStroke.neutral};
  border-radius: ${uiTokens.radius.large}px;
  padding: ${uiTokens.spacing.padding.large}px ${uiTokens.spacing.padding.medium}px;
  background: ${({ isDragging }) =>
    isDragging ? uiTokens.uiSurface.pressedOrSelected : uiTokens.uiSurface['em-static']};
  display: flex;
  height: 164px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: ${uiTokens.spacing.gap.semiMedium}px;
  width: 100%;
`

const StyledLabel = styled('label')`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const HiddenInput = styled('input')`
  display: none;
`

const UploadText = styled('div')`
  display: flex;
  align-items: center;
  padding-bottom: ${uiTokens.spacing.gap.small}px;
  color: ${uiTokens.uiContent.emphasized};
  font-family: ${uiTokens.font.fontFamily.default};
  font-size: ${uiTokens.font.fontSize.title.large}px;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  line-height: ${uiTokens.font.lineHeight.title.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.loose}px;
`

const UploadLink = styled('span')`
  color: ${uiTokens.uiContent.accent};
  font-family: ${uiTokens.font.fontFamily.default};
  font-size: ${uiTokens.font.fontSize.title.large}px;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  line-height: ${uiTokens.font.lineHeight.title.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.loose}px;
`

const FileTypes = styled('div')`
  color: ${uiTokens.uiContent.neutral};
  font-family: ${uiTokens.font.fontFamily.default};
  font-size: ${uiTokens.font.fontSize.uiControlText.large}px;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.medium};
  line-height: ${uiTokens.font.lineHeight.uiControlText.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal}px;
`
const StyledIconSelector = styled(IconSelector)`
  display: flex;
  width: ${uiTokens.sizing.uiControl.height.xSmall}px;
  height: ${uiTokens.sizing.icon.medium}px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`
const StyledTypography = styled(Typography)`
  color: ${uiTokens.uiContent.accent};
  font-family: ${uiTokens.font.fontFamily.default};
  font-size: ${uiTokens.font.fontSize.uiControlText.medium}px;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  line-height: ${uiTokens.font.lineHeight.uiControlText.medium}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal}px;
  padding-left: ${uiTokens.spacing.padding.xSmall}px;
`

const StyledUploadedFileCard = styled(Card)`
  &.MuiPaper-root {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${uiTokens.spacing.gap.semiMedium}px;
    width: 100%;
    box-sizing: border-box;
    padding: ${uiTokens.spacing.padding.medium}px;
    border-radius: ${uiTokens.radius.large}px;
    border: ${uiTokens.sizing.stroke.s}px solid ${uiTokens.uiStroke.neutral};
    background: ${uiTokens.uiSurface['em-static']};
    box-shadow: none;
    min-height: 90px;
  }
`

const StyledUploadedFileIconWrap = styled('div')`
  align-items: center;
  width: 32px;
  height: 32px;
  color: ${uiTokens.uiContent.emphasized};
`

const StyledUploadedFileBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${uiTokens.spacing.gap.small}px;
  width: 100%;
`

const StyledUploadedFileTitle = styled(CardHeader)`
  padding: 0;
  .MuiCardHeader-title {
    color: ${uiTokens.uiContent.accent};
    font-family: ${uiTokens.font.fontFamily.default};
    font-size: ${uiTokens.font.fontSize.body.large}px;
    font-style: normal;
    font-weight: ${uiTokens.font.fontWeight.regular};
    line-height: ${uiTokens.font.lineHeight.body.large}px; /* 150% */
    letter-spacing: ${uiTokens.font.letterSpacing.mediumLoose}px;
  }
`

const StyledUploadedFileInfo = styled(CardContent)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${uiTokens.spacing.gap.medium}px;
  color: ${uiTokens.uiContent.neutral};
  font-size: ${uiTokens.font.fontSize.body.medium}px;
  line-height: ${uiTokens.font.lineHeight.body.medium}px;
  padding: 0;
  margin: 0;
  &:last-child {
    padding-bottom: 0;
  }
`
const StyledUploadedFileSize = styled(Typography)`
  color: ${uiTokens.uiContent.neutral};
  font-family: ${uiTokens.font.fontFamily.default};
  font-size: ${uiTokens.font.fontSize.body.large}px;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.regular};
  line-height: ${uiTokens.font.lineHeight.body.large}px; /* 150% */
  letter-spacing: ${uiTokens.font.letterSpacing.normal}px;
`

const StyledUploadedFileStatus = styled(Typography, { shouldForwardProp: (prop) => prop !== '$isUploadCompleted' })<{
  $isUploadCompleted: boolean
}>`
  color: ${({ $isUploadCompleted }) => ($isUploadCompleted ? uiTokens.uiContent.success : uiTokens.uiContent.error)};
  font-family: ${uiTokens.font.fontFamily.default};
  font-size: ${uiTokens.font.fontSize.body.large}px;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.medium};
  line-height: ${uiTokens.font.lineHeight.body.large}px; /* 150% */
  letter-spacing: ${uiTokens.font.letterSpacing.mediumLoose}px;
  display: inline-flex;
  align-items: center;
  &::before {
    content: '•';
    display: block;
    font-size: 28px;
    line-height: 1;
    margin-right: ${uiTokens.spacing.gap.small}px;
  }
`

export function PhotoDragAndDrop({
  accept,
  maxSize: _maxSize,
  onFiles,
  onFilesReject,
  selectedFile,
  onClear,
  isUploadCompleted = false,
}: PhotoDragAndDropProps) {
  const t = useTranslation()
  const acceptedFileTypes = accept?.split(',').map((x) => x.trim()) ?? []
  const { isDragging, handleDrop, handleDragOver, handleDragLeave, handleInput } = useFileDropzone({
    accept: acceptedFileTypes,
    onFiles,
    onFilesReject,
  })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const labelDragAndDrop1 = t('USM_LOGO_CLICK_TO_UPLOAD', 'Choose a photo or')
  const labelDragAndDrop2 = t('USM_LOGO_OR_DRAG_AND_DROP', 'drag and drop')
  const labelDragAndDrop3 = t('USM_LOGO_OR_DRAG_AND_DROP', 'it here')
  const acceptString = acceptedFileTypes.join(',')

  const generateSizeLabel = (size: number | undefined): string => {
    if (!size) return ''
    if (size < 1024) return `${size}B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)}KB`
    return `${(size / (1024 * 1024)).toFixed(2)}MB`
  }

  return (
    <>
      {!selectedFile && (
        <DndContext>
          <DropArea
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            isDragging={isDragging}
          >
            <StyledLabel>
              <HiddenInput ref={fileInputRef} type="file" accept={acceptString} onChange={handleInput} />
              <UploadText>
                {labelDragAndDrop1}&nbsp;<UploadLink>{labelDragAndDrop2}</UploadLink>&nbsp;{labelDragAndDrop3}
              </UploadText>
              <FileTypes>{t('LBL_NGID_UPLOAD_PHOTO_LIMIT', 'PNG or JPG (max. 5MB)')}</FileTypes>
            </StyledLabel>
            <ButtonSecondary onClick={() => fileInputRef.current?.click()}>
              <StyledIconSelector iconName="Upload" color={uiTokens.uiContent.accent} />
              <StyledTypography>{t('LBL_NGID_UPLOAD_PHOTO_TITLE', 'Upload photo')}</StyledTypography>
            </ButtonSecondary>
          </DropArea>
        </DndContext>
      )}
      {selectedFile && (
        <StyledUploadedFileCard>
          <StyledUploadedFileIconWrap>
            <AttachFileIcon fontSize="medium" />
          </StyledUploadedFileIconWrap>
          <StyledUploadedFileBody>
            <StyledUploadedFileTitle title={selectedFile.name} />
            <StyledUploadedFileInfo>
              <StyledUploadedFileSize>{generateSizeLabel(selectedFile.size)}</StyledUploadedFileSize>
              <StyledUploadedFileStatus $isUploadCompleted={isUploadCompleted}>
                {isUploadCompleted
                  ? t('LBL_NGID_COMPLETED_LBL', 'Completed')
                  : t('LBL_NGID_UPLOAD_PHOTO_ERROR', 'Uploaded photo cannot be used')}
              </StyledUploadedFileStatus>
            </StyledUploadedFileInfo>
          </StyledUploadedFileBody>
          <ButtonIcon
            color="primary"
            aria-label="Clear"
            onClick={() => {
              onClear()
            }}
            edge="end"
          >
            <IconSelector iconName="DeleteOutlineOutlined" />
          </ButtonIcon>
        </StyledUploadedFileCard>
      )}
    </>
  )
}
